import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DialogContent,
  TextField,
  Button,
  InputAdornment,
  Typography,
  IconButton,
  Box,
} from '@mui/material';
import { ArrowForwardRounded, LinkRounded } from '@mui/icons-material';
import ProcessHandler from '@src/components/ProcessHandler';
import Dialog from '@src/components/Dialog';
import { isValidHttpUrl } from '@src/utils/checkValid';
import { LIST_ICON_WEB_SAMPLE } from '@src/constants/upload';
import { COLOR } from '@src/styles/color';
import debounce from '@src/utils/debounce';
import IconClose from '@src/assets/icons/icon-close.png';
import ImgNoThumbnail from '@src/assets/images/no-thumbnail.jpg';
import apis from '@src/apis';
import CommonDialog from '@src/new-components/Dialog/CommonDialog';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { useSelector } from 'react-redux';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import { trackEvent } from '@src/services/moengage';
import { countImportedContentLength } from '@src/services/tts';
import {
  MOE_EVENTS,
  MOE_DOCUMENT_TYPE,
  MOE_PROJECT_SOURCE_TYPE,
} from '@src/constants/moengage';
import CommonButton from '@src/new-components/Button';
import {
  StyleBoxAddSuccess,
  StyleButtonSuccess,
  StyledHandleByNewUrl,
} from './index.style';

const NewImportURL = ({ open, onClose, onHandleUploadContent }) => {
  const [link, setLink] = useState('');
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [datacrawlers, setDataCrawlers] = useState(null);
  const { t } = useTranslation();
  const { user } = useSelector((state) => state.auth);
  const { getFeatureValue } = useFeatureFlags();

  const newUiProjectsEnabled = getFeatureValue(FEATURE_KEYS.NEW_UI_PROJECTS, {
    userId: user.id,
    email: user.email,
    phoneNumber: user.phoneNumber,
  });

  const handleChange = (e) => {
    setError(false);
    setLink(e.target.value);
  };

  const handleClose = () => {
    onClose();
    setError(false);
    setLink('');
    setLoading(false);
    setDataCrawlers(null);
  };

  const handleClear = () => {
    setError(false);
    setLink('');
    setLoading(false);
    setDataCrawlers(null);
  };

  const handleCrawNewsByUrl = async () => {
    // Reset error state when user input a new link with debounce
    setError(false);

    if (!link || !isValidHttpUrl(link)) {
      setError(true);
      return;
    }
    setLoading(true);
    const data = await apis.upload.crawlNewsByUrl(link);
    setLoading(false);
    if (data && data.status) {
      setError(false);
      setDataCrawlers(data);
    } else {
      setError(true);
    }
  };

  useEffect(() => {
    if (link) {
      debounce(handleCrawNewsByUrl, 500)(link);
    }
  }, [link]);

  const boxAddSuccess = () => (
    <StyleBoxAddSuccess>
      <div className="wrap-file">
        {datacrawlers.results?.thumbnail ? (
          <img
            src={datacrawlers.results.thumbnail}
            alt="thumbnail"
            className="img-thumbnail"
          />
        ) : (
          <img
            src={ImgNoThumbnail}
            alt="no thumbnail"
            className="img-thumbnail"
          />
        )}
        <Typography variant="body1" className="success-file-name">
          {datacrawlers.results?.title}
        </Typography>
        <IconButton
          data-id="icon-remove-web-link"
          onClick={() => handleClear()}
        >
          <img src={IconClose} alt="file-word" className="clear-icon" />
        </IconButton>
      </div>
    </StyleBoxAddSuccess>
  );

  const boxImportByUrl = () => (
    <StyledHandleByNewUrl>
      <Box className="handle-url-content">
        <TextField
          error={error}
          helperText={error && t('errorHandleByUrl')}
          fullWidth
          color="secondary"
          classes={{ input: 'paste-input' }}
          placeholder={t('exampleValidUrl')}
          value={link}
          onChange={handleChange}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: COLOR.white,
            },
          }}
          InputProps={{
            inputProps: {
              'data-id': 'import-via-link',
            },
            sx: { borderRadius: '8px', height: '40px' },
            startAdornment: (
              <InputAdornment position="start">
                <LinkRounded />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <ProcessHandler loading={loading} align="center" size={24} />
              </InputAdornment>
            ),
          }}
        />
        <div className="wrap-icon">
          {LIST_ICON_WEB_SAMPLE.map((item) => (
            <img
              key={item.alt}
              src={item.icon}
              alt={item.alt}
              className="file-icon"
            />
          ))}
        </div>
      </Box>
    </StyledHandleByNewUrl>
  );

  if (newUiProjectsEnabled) {
    return (
      <CommonDialog
        open={open}
        onClose={onClose}
        title={t('uploadYourFile')}
        disableAutoClose
        width="640px"
        contentComponent={
          <>
            {datacrawlers && datacrawlers.status
              ? boxAddSuccess()
              : boxImportByUrl()}
            <CommonButton
              variant="contained"
              color="primary"
              onClick={async () => {
                await onHandleUploadContent(
                  datacrawlers.results.text,
                  MOE_PROJECT_SOURCE_TYPE.URL,
                );
                trackEvent(MOE_EVENTS.DOCUMENT_IMPORTED, {
                  document_type: MOE_DOCUMENT_TYPE.URL,
                  content_length: countImportedContentLength(
                    datacrawlers.results?.text || '',
                  ),
                });
                handleClose();
              }}
              disabled={loading || !datacrawlers}
              customStyle={{
                height: '40px',
                borderRadius: '8px',
                padding: '8px 24px',
                marginLeft: 'auto',
                marginTop: '16px',
              }}
              textProps={{
                variantText: 'body-14-semibold',
                color: COLOR.darkBlue,
              }}
            >
              {t('continue')}
            </CommonButton>
          </>
        }
      />
    );
  }

  return (
    <Dialog
      title={t('webLink')}
      open={open}
      fullWidth
      onClose={handleClose}
      dataAttribute={{ 'data-id': 'close-import-via-link' }}
    >
      <DialogContent
        dividers
        sx={{
          '&.MuiDialogContent-root': {
            paddingTop: '24px !important',
          },
        }}
      >
        {datacrawlers && datacrawlers.status
          ? boxAddSuccess()
          : boxImportByUrl()}
        <StyleButtonSuccess>
          <Button
            variant="contained"
            color="primary"
            className="btn-continue"
            data-id="button-web-link-continue"
            onClick={async () => {
              await onHandleUploadContent(
                datacrawlers.results.text,
                MOE_PROJECT_SOURCE_TYPE.URL,
              );
              trackEvent(MOE_EVENTS.DOCUMENT_IMPORTED, {
                document_type: MOE_DOCUMENT_TYPE.URL,
                content_length: countImportedContentLength(
                  datacrawlers.results?.text || '',
                ),
              });
              handleClose();
            }}
            endIcon={<ArrowForwardRounded />}
            sx={{
              '&.Mui-disabled': {
                background: COLOR.yellow[32],
                color: '#3c164e99',
              },
            }}
            disabled={loading || !datacrawlers}
          >
            {t('continue')}
          </Button>
        </StyleButtonSuccess>
      </DialogContent>
    </Dialog>
  );
};

export default NewImportURL;
